import React, { useEffect, useState } from 'react';
import { Modal, FormGroup, FormControl, ControlLabel, Checkbox, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { patchCapLightsPolicy } from '../utils/bedrockAPI';
import './LightsPolicyModal.css';

const DEFAULT_LIGHTS_POLICY = {
  version: 1,
  automation_mode: "motion_night_only",
  disabled_monitors: [],
  manual_overrides: {},
  lights_globally_disabled: false,
  effect: {type: "none"},
  motion_lights_hold_seconds: 30
};

/** Full policy object for PATCH /caps/:id when enabling lights from an empty `{}` cap. */
export const getDefaultLightsPolicy = () => ({
  version: DEFAULT_LIGHTS_POLICY.version,
  automation_mode: DEFAULT_LIGHTS_POLICY.automation_mode,
  disabled_monitors: [],
  manual_overrides: {},
  lights_globally_disabled: DEFAULT_LIGHTS_POLICY.lights_globally_disabled,
  effect: {...DEFAULT_LIGHTS_POLICY.effect},
  motion_lights_hold_seconds: DEFAULT_LIGHTS_POLICY.motion_lights_hold_seconds
});

const AUTOMATION_MODE_OPTIONS = [
  {value: "motion_night_only", label: "Motion at night only"},
  {value: "motion_day_only", label: "Motion during the day only"},
  {value: "motion_always", label: "Motion any time"},
  {value: "always_on", label: "Always on"},
  {value: "always_on_night", label: "Always on at night"},
  {value: "always_on_day", label: "Always on during the day"},
  {value: "always_off", label: "Always off"}
];

const StyledModalTitle = styled(Modal.Title)`
    font-family: inherit;
    font-size: 24px;
    font-weight: 500;
`;

const isPlainObject = (value) => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

export const hasLightsPolicyConfig = (lights) => {
  return isPlainObject(lights) && Object.keys(lights).length > 0;
};

const lightsPolicyFormFromCap = (lights) => {
  const policy = isPlainObject(lights) ? lights : {};
  const disabledMonitors = Array.isArray(policy.disabled_monitors) ? policy.disabled_monitors.map(String) : [];

  return {
    version: policy.version || DEFAULT_LIGHTS_POLICY.version,
    automation_mode: policy.automation_mode || DEFAULT_LIGHTS_POLICY.automation_mode,
    disabled_monitors: disabledMonitors,
    lights_globally_disabled: !!policy.lights_globally_disabled
  };
};

export default function LightsPolicyModal({ show, onHide, cap, monitorImages, setCap, setErrorMessage, setErrorStatus, setShowError }) {
  const [lightsPolicyForm, setLightsPolicyForm] = useState(lightsPolicyFormFromCap(null));
  const [lightsPolicyError, setLightsPolicyError] = useState("");
  const [isSavingLightsPolicy, setIsSavingLightsPolicy] = useState(false);
  const [previousAutomationMode, setPreviousAutomationMode] = useState(DEFAULT_LIGHTS_POLICY.automation_mode);

  useEffect(() => {
    if(show && cap) {
      const nextForm = lightsPolicyFormFromCap(cap.lights);
      setLightsPolicyForm(nextForm);
      setPreviousAutomationMode(nextForm.automation_mode === "always_off" ? DEFAULT_LIGHTS_POLICY.automation_mode : nextForm.automation_mode);
      setLightsPolicyError("");
    }
  }, [show, cap]);

  const showAppMessage = (message, status) => {
    if(setErrorMessage && setErrorStatus && setShowError) {
      setErrorMessage(message);
      setErrorStatus(status);
      setShowError(true);
    }
  };

  const updateLightsPolicyForm = (field, value) => {
    setLightsPolicyError("");
    if(field === "automation_mode" && value !== "always_off") {
      setPreviousAutomationMode(value);
    }
    setLightsPolicyForm(prevForm => ({
      ...prevForm,
      [field]: value
    }));
  };

  const updateLightsGloballyDisabled = (disabled) => {
    setLightsPolicyError("");
    setLightsPolicyForm(prevForm => {
      if(disabled && prevForm.automation_mode !== "always_off") {
        setPreviousAutomationMode(prevForm.automation_mode);
      }

      return {
        ...prevForm,
        automation_mode: disabled ? "always_off" : previousAutomationMode,
        lights_globally_disabled: disabled
      };
    });
  };

  const toggleEnabledLight = (monitorId) => {
    const normalizedMonitorId = String(monitorId);
    setLightsPolicyError("");
    setLightsPolicyForm(prevForm => {
      const isEnabled = !prevForm.disabled_monitors.includes(normalizedMonitorId);
      const disabledMonitors = isEnabled
        ? [...prevForm.disabled_monitors, normalizedMonitorId]
        : prevForm.disabled_monitors.filter(id => id !== normalizedMonitorId);

      return {
        ...prevForm,
        disabled_monitors: disabledMonitors
      };
    });
  };

  const getLightsMonitorOptions = () => {
    const options = [];
    const seenMonitorIds = {};

    if(monitorImages) {
      Object.entries(monitorImages)
        .filter(([, monitorData]) => {
          if(cap.architecture && cap.architecture.includes('2CAM')) {
            return monitorData.id <= 2;
          }
          return true;
        })
        .sort(([, a], [, b]) => Number(a.id) - Number(b.id))
        .forEach(([monitorId, monitorData]) => {
          const normalizedMonitorId = String(monitorId);
          seenMonitorIds[normalizedMonitorId] = true;
          options.push({
            monitorId: normalizedMonitorId,
            label: `Monitor ${monitorData.id || normalizedMonitorId}`
          });
        });
    }

    lightsPolicyForm.disabled_monitors.forEach(monitorId => {
      if(!seenMonitorIds[monitorId]) {
        options.push({
          monitorId,
          label: `Monitor ${monitorId}`
        });
      }
    });

    return options;
  };

  const handleLightsPolicySave = async () => {
    const existingLights = isPlainObject(cap.lights) ? cap.lights : {};
    const existingMotionHoldSeconds = Number(existingLights.motion_lights_hold_seconds);
    const motionHoldSeconds = Number.isNaN(existingMotionHoldSeconds)
      ? DEFAULT_LIGHTS_POLICY.motion_lights_hold_seconds
      : existingMotionHoldSeconds;
    const nextLightsPolicy = {
      ...existingLights,
      version: Number(lightsPolicyForm.version) || DEFAULT_LIGHTS_POLICY.version,
      automation_mode: lightsPolicyForm.automation_mode,
      disabled_monitors: lightsPolicyForm.disabled_monitors,
      manual_overrides: isPlainObject(existingLights.manual_overrides) ? existingLights.manual_overrides : DEFAULT_LIGHTS_POLICY.manual_overrides,
      lights_globally_disabled: lightsPolicyForm.lights_globally_disabled,
      effect: isPlainObject(existingLights.effect) ? existingLights.effect : DEFAULT_LIGHTS_POLICY.effect,
      motion_lights_hold_seconds: motionHoldSeconds
    };

    try {
      setIsSavingLightsPolicy(true);
      setLightsPolicyError("");
      await patchCapLightsPolicy(cap.capId, nextLightsPolicy);
      setCap(prevCap => ({
        ...prevCap,
        lights: nextLightsPolicy
      }));
      onHide();
      showAppMessage("Light settings saved!", "success-message");
    } catch (error) {
      console.error('Error updating light settings:', error);
      setLightsPolicyError("Could not save light settings. Please try again.");
      showAppMessage("Could not save light settings", "error-message");
    } finally {
      setIsSavingLightsPolicy(false);
    }
  };

  const handleHide = () => {
    if(!isSavingLightsPolicy) {
      onHide();
    }
  };

  const monitorOptions = getLightsMonitorOptions();

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <StyledModalTitle>Light Settings for {cap?.name}</StyledModalTitle>
      </Modal.Header>
      <Modal.Body>
        <div className="lights-policy-modal">
          {lightsPolicyError && (
            <div className="lights-policy-error">{lightsPolicyError}</div>
          )}
          <FormGroup controlId="lightsAutomationMode">
            <ControlLabel>Automation mode</ControlLabel>
            <FormControl
              componentClass="select"
              value={lightsPolicyForm.automation_mode}
              onChange={(e) => updateLightsPolicyForm("automation_mode", e.target.value)}
              disabled={lightsPolicyForm.lights_globally_disabled}
            >
              {AUTOMATION_MODE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormControl>
          </FormGroup>
          <FormGroup controlId="lightsGlobalDisable">
            <div className="lights-policy-checkbox-container">
              <Checkbox
                className="lights-policy-checkbox"
                checked={lightsPolicyForm.lights_globally_disabled}
                onChange={(e) => updateLightsGloballyDisabled(e.target.checked)}
              >
                Disable all lights
              </Checkbox>
            </div>
            <div className="lights-policy-help">
              Prevent motion or automation from turning lights on.
            </div>
          </FormGroup>
          <FormGroup>
            <ControlLabel>Enabled lights</ControlLabel>
            <div className="lights-policy-help">
              Choose which monitor lights can turn on from motion or automation.
            </div>
            <div className="lights-monitor-grid">
              {monitorOptions.length > 0 ? monitorOptions.map(option => (
                <Checkbox
                  className="lights-policy-checkbox"
                  key={option.monitorId}
                  checked={!lightsPolicyForm.disabled_monitors.includes(option.monitorId)}
                  onChange={() => toggleEnabledLight(option.monitorId)}
                >
                  {option.label}
                </Checkbox>
              )) : (
                <div className="lights-policy-help">Monitor list is loading.</div>
              )}
            </div>
          </FormGroup>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleHide} disabled={isSavingLightsPolicy}>Cancel</Button>
        <Button bsStyle="success" onClick={handleLightsPolicySave} disabled={isSavingLightsPolicy}>
          {isSavingLightsPolicy ? "Saving..." : "Save Light Settings"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
